import { useEffect, useState } from 'react'
import { Outlet, createRootRoute, useLocation, Navigate } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { useAuth0 } from '@auth0/auth0-react'
import { AppErrorBoundary } from '@/components/ErrorBoundary'
import { NoAccessNotice } from '@/components/NoAccessNotice'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { ChevronDown, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Link } from '@tanstack/react-router'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { usePermissions } from '@/hooks/usePermissions'
import { useActivityStats } from '@/hooks/useActivityStats'
import { OwnWidgetEmbed } from '@/components/OwnWidgetEmbed'
import { Toaster } from '@/components/ui/sonner'
import { NAV_GROUPS, requiredPermissionFor, firstAllowedPath } from '@/lib/navigation'
import {
  MobileHeaderActionProvider,
  useMobileHeaderAction,
} from '@/contexts/MobileHeaderActionContext'
import { useIsMobile } from '@/hooks/use-mobile'

export const Route = createRootRoute({
  component: RootLayout,
})

function FullScreenSpinner({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-dvh">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
        <p className="mt-2">{message}</p>
      </div>
    </div>
  )
}

// Seitentitel je Route — versorgt sowohl document.title als auch die mobile Kopfleiste
const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/activities': 'Aktivität',
  '/calls': 'Anrufe',
  '/chats': 'Chats',
  '/callbacks': 'Tickets',
  '/contacts': 'Kontakte',
  '/kampangen': 'Kampagnen',
  '/bookings': 'Buchungen',
  '/assistant': 'AudavoGPT',
  '/agent-configuration': 'Agent',
  '/appointments-configuration': 'Termine',
  '/calendar-configuration': 'Kalender',
  '/transfer-destinations': 'Weiterleitungen',
  '/chat-widgets': 'Chat Widgets',
  '/booking-keys': 'Buchungsseiten',
  '/integrations': 'Integrationen',
  '/knowledge-base': 'Wissensdatenbank',
  '/settings': 'Einstellungen',
  '/organization': 'Organisation',
  '/admin/billing': 'Abrechnung',
  '/admin/organizations': 'Organisationen',
  '/admin/telephony': 'Telefonie',
  '/admin/voices': 'Stimmen',
  '/google-calendar-callback': 'Google Kalender',
}

function pageTitleFor(pathname: string): string | undefined {
  return (
    PAGE_TITLES[pathname]
    || Object.entries(PAGE_TITLES).find(([path]) => pathname.startsWith(path + '/'))?.[1]
  )
}

function RootLayout() {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()
  const { isLoading: permissionsLoading } = usePermissions()

  if (isLoading) {
    return <FullScreenSpinner message="Loading..." />
  }

  if (!isAuthenticated) {
    loginWithRedirect()
    return <FullScreenSpinner message="Redirecting to login..." />
  }

  // Wait for permissions before rendering the shell so the sidebar and
  // route guard decide once, without flickering
  if (permissionsLoading) {
    return <FullScreenSpinner message="Loading..." />
  }

  return <AppShell />
}

// Der Navigationsinhalt liegt in einer eigenen Komponente: AppShell rendert den
// SidebarProvider selbst und haette dort noch keinen Sidebar-Kontext.
function AppSidebar() {
  const location = useLocation()
  const { logout, user } = useAuth0()
  const { hasPermission } = usePermissions()
  const { data: activityStats } = useActivityStats()
  const { setOpenMobile } = useSidebar()

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => hasPermission(item.permission)),
  })).filter((group) => group.items.length > 0)

  const defaultGroupId = visibleGroups[0]?.id ?? ''
  const [openGroup, setOpenGroup] = useState<string>(defaultGroupId)

  // Auf Mobile liegt die Navigation als Sheet ueber dem Inhalt und wird beim
  // Routenwechsel nicht unmountet — nach dem Tap also selbst schliessen.
  // Auf Desktop ist openMobile ohnehin immer false, der Aufruf ist dort wirkungslos.
  const closeMobileSidebar = () => setOpenMobile(false)

  return (
    <Sidebar>
      <SidebarHeader className="border-b p-2 flex">
        <img src="/audavo.png" alt="Audavo" className="w-[70%] h-auto" />
      </SidebarHeader>
      <SidebarContent>
        {visibleGroups.map((group) => (
          <Collapsible
            key={group.id}
            open={openGroup === group.id}
            onOpenChange={(open) =>
              setOpenGroup(open ? group.id : group.id === defaultGroupId ? '' : defaultGroupId)
            }
            className="group/collapsible"
          >
            <SidebarGroup>
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger>
                  {group.label}
                  <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => {
                      const isActive =
                        location.pathname === item.path ||
                        location.pathname.startsWith(item.path + '/') ||
                        (item.activePrefixes?.some((prefix) => location.pathname.startsWith(prefix)) ?? false)
                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton asChild isActive={isActive}>
                            <Link to={item.path} onClick={closeMobileSidebar}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                          {item.path === '/activities' && activityStats && activityStats.totalUnacknowledged > 0 && (
                            <SidebarMenuBadge
                              className="bg-primary text-primary-foreground border-none right-2 peer-hover/menu-button:bg-primary peer-hover/menu-button:text-primary-foreground peer-data-[active=true]/menu-button:bg-primary peer-data-[active=true]/menu-button:text-primary-foreground"
                              aria-label={`${activityStats.totalUnacknowledged} ungelesene Aktivitäten`}
                            >
                              {activityStats.totalUnacknowledged > 99 ? '99+' : activityStats.totalUnacknowledged}
                            </SidebarMenuBadge>
                          )}
                        </SidebarMenuItem>
                      )
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="border-t pt-2 mt-2">
              <div className="px-2 py-1 text-xs text-muted-foreground">
                {user?.name || user?.email}
              </div>
              <SidebarMenuButton
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <LogOut className="h-4 w-4" />
                <span>Abmelden</span>
              </SidebarMenuButton>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

// Aktions-Slot rechts in der mobilen Kopfleiste. Steht in einer eigenen Komponente,
// damit nur sie am Kontext haengt und nicht die ganze Shell neu rendert.
function MobileHeaderAction() {
  const action = useMobileHeaderAction()

  if (!action) return null

  return (
    <Button
      variant="ghost"
      size="icon"
      className="-mr-2 size-10"
      onClick={action.onClick}
      aria-label={action.label}
      title={action.label}
    >
      <action.icon className="h-4 w-4" />
    </Button>
  )
}

function AppShell() {
  const location = useLocation()
  const { hasPermission } = usePermissions()
  const isMobile = useIsMobile()

  const pageTitle = pageTitleFor(location.pathname)

  useEffect(() => {
    document.title = pageTitle ? `audavo - ${pageTitle}` : 'audavo'
  }, [pageTitle])

  // Central route guard: redirect away from routes the user has no permission for
  const requiredPermission = requiredPermissionFor(location.pathname)
  const isAllowed = !requiredPermission || hasPermission(requiredPermission)
  const fallbackPath = firstAllowedPath(hasPermission)

  return (
    <SidebarProvider>
      <MobileHeaderActionProvider>
        <div className="flex h-dvh w-full">
          <AppSidebar />
          <main className="flex-1 overflow-hidden flex flex-col">
            {/* Die Kopfleiste ist auf Mobile der einzige Zugang zur Navigation und bleibt
                deshalb ausserhalb des Scrollbereichs stehen. pt-safe waere hier falsch:
                es setzt padding-top komplett und wuerde py-2 ohne Notch auf 0 druecken. */}
            <header className="md:hidden shrink-0 flex items-center gap-2 px-4 py-2 pt-[max(env(safe-area-inset-top,0px),0.5rem)] border-b">
              <SidebarTrigger className="size-10 -ml-2" />
              {/* min-w-0: sonst schiebt ein langer Titel die Aktion aus der Leiste */}
              <h2 className="min-w-0 flex-1 truncate text-lg font-semibold">{pageTitle ?? 'Audavo'}</h2>
              <MobileHeaderAction />
            </header>
            {/* Mobil scrollt der Inhalt hier, ab md gilt wieder das Desktop-Layout mit
                Innen-Scroll je Seite. `relative` traegt den Page-Error-Fallback. */}
            <div className="relative flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
              <AppErrorBoundary variant="page" showNavigateHome>
                {isAllowed ? (
                  <Outlet />
                ) : fallbackPath ? (
                  <Navigate to={fallbackPath} replace />
                ) : (
                  <NoAccessNotice />
                )}
              </AppErrorBoundary>
            </div>
          </main>
        </div>
      </MobileHeaderActionProvider>
      <TanStackRouterDevtools />
      {/* Unten rechts sitzt auf Mobile die Widget-Bubble; dieselbe Breakpoint-Query
          wie Tailwinds `md:`, damit beides an derselben Stelle umschaltet. */}
      <Toaster richColors closeButton position={isMobile ? 'top-center' : 'bottom-right'} />
      <OwnWidgetEmbed />
    </SidebarProvider>
  )
}
